
import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { generateUniqueSlug } from "@/lib/supabase/shops";
import { Shop } from "@/components/dashboard/shops/types";

interface ProfileSectionFormProps {
  shop: Shop;
  isEditing: boolean;
  isSaving: boolean;
  isGeneratingSlug: boolean;
  name: string;
  setName: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  category: string;
  setCategory: (value: string) => void;
  logoUrl: string;
  setLogoUrl: (value: string) => void;
  slug: string;
  setSlug: (value: string) => void;
  handleSave: () => Promise<void>;
}

export const ProfileSectionForm: React.FC<ProfileSectionFormProps> = ({
  shop,
  isEditing,
  isSaving,
  isGeneratingSlug,
  name,
  setName,
  description,
  setDescription,
  category,
  setCategory,
  logoUrl,
  setLogoUrl,
  slug,
  setSlug,
  handleSave
}) => {
  // Générer automatiquement un slug quand le nom change
  useEffect(() => {
    const generateSlug = async () => {
      if (isEditing && name && (!slug || slug !== shop.slug)) {
        try {
          const uniqueSlug = await generateUniqueSlug(name);
          setSlug(uniqueSlug);
        } catch (error) {
          console.error('Erreur lors de la génération du slug:', error);
          // En cas d'erreur, utiliser la méthode simple
          const simpleSlug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
          setSlug(simpleSlug);
        }
      }
    };
    
    generateSlug();
  }, [name, isEditing, shop.slug, slug, setSlug]);

  return (
    <div className="space-y-6">
      <FormField
        label="Nom De La Boutique"
        id="shop-name"
        isEditing={isEditing}
        editingContent={
          <Input 
            id="shop-name" 
            value={name} 
            onChange={(e) => setName(e.target.value)} 
            placeholder="Nom de la boutique"
          />
        }
        displayContent={
          <div className="text-lg font-medium text-gray-800 dark:text-gray-200">{name}</div>
        }
      />
      
      <FormField
        label="Slug URL"
        id="shop-slug"
        isEditing={isEditing}
        editingContent={
          <div className="space-y-1">
            <div className="relative">
              <Input 
                id="shop-slug" 
                value={slug} 
                onChange={(e) => setSlug(e.target.value)} 
                placeholder="slug-de-la-boutique"
                disabled={isGeneratingSlug}
              />
              {isGeneratingSlug && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="h-4 w-4 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500">Le slug est utilisé dans l'URL (ex: votre-site.com/{slug})</p>
          </div>
        }
        displayContent={
          <div className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
            <span>{slug || "Non défini"}</span>
            {slug && (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                (URL: /{slug})
              </span>
            )}
          </div>
        }
      />
      
      <FormField
        label="Description"
        id="shop-description"
        isEditing={isEditing}
        editingContent={
          <Textarea 
            id="shop-description" 
            value={description} 
            onChange={(e) => setDescription(e.target.value)} 
            placeholder="Description de la boutique"
            rows={4}
          />
        }
        displayContent={
          <div className="text-gray-600 dark:text-gray-400">
            {description || "Aucune description disponible"}
          </div>
        }
      />
      
      <FormField
        label="Catégorie"
        id="shop-category"
        isEditing={isEditing}
        editingContent={
          <Input 
            id="shop-category" 
            value={category} 
            onChange={(e) => setCategory(e.target.value)} 
            placeholder="Ex: Vêtements, Accessoires..."
          />
        }
        displayContent={
          <div className="text-gray-600 dark:text-gray-400">
            {category || "Non spécifié"}
          </div>
        }
      />
      
      <FormField
        label="Logo URL"
        id="shop-logo"
        isEditing={isEditing}
        editingContent={
          <Input 
            id="shop-logo" 
            value={logoUrl} 
            onChange={(e) => setLogoUrl(e.target.value)} 
            placeholder="URL du logo"
          />
        }
        displayContent={
          <div className="text-gray-600 dark:text-gray-400">
            {logoUrl ? (
              <div className="flex items-center gap-2">
                <img src={logoUrl} alt="Logo" className="w-10 h-10 rounded-full object-cover" />
                <span className="text-sm text-gray-500 truncate">{logoUrl}</span>
              </div>
            ) : (
              "Aucun logo défini"
            )}
          </div>
        }
      />
      
      {isEditing && (
        <div className="flex justify-end pt-4">
          <Button 
            onClick={handleSave} 
            disabled={isSaving || isGeneratingSlug}
            className="flex items-center gap-2"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isSaving ? "Enregistrement..." : "Enregistrer les modifications"}
          </Button>
        </div>
      )}
    </div>
  );
};

interface FormFieldProps {
  label: string;
  id: string;
  isEditing: boolean;
  editingContent: React.ReactNode;
  displayContent: React.ReactNode;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  id,
  isEditing,
  editingContent,
  displayContent
}) => {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {isEditing ? editingContent : displayContent}
    </div>
  );
};
